/* eslint-disable max-lines-per-function */
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  ScrollArea,
} from '@e2n/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { useCreateTermineFormContext } from '@/domain/termine/features/CreateTermin/shared/useCreateTermineFormContext';
import { TerminKalender } from '@/domain/termine/shared/types/TerminKalender';
import { FieldBase } from '@/components/form/fieldBase';
import { Markierung } from '@/domain/termine/components';
import {
  markierungGermanMap,
  markierungMapper,
} from '@/domain/termine/shared/types/Markierung';
import { useState } from 'react';

type CalendarSelectItemProps = {
  kalenderItem: TerminKalender;
  isSelected: boolean;
};

function CalendarSelectItem({
  kalenderItem,
  isSelected,
}: CalendarSelectItemProps) {
  return (
    <>
      <div className="flex items-center gap-2">
        {kalenderItem.markierung &&
          kalenderItem.id !== 'NO_KALENDER' &&
          kalenderItem.markierung !== markierungGermanMap['LIGHTGRAY'] && (
            <Markierung
              color={markierungMapper[kalenderItem.markierung].base}
            />
          )}
        <span className="text-sm font-medium">{kalenderItem.name}</span>
      </div>

      {isSelected && (
        <FontAwesomeIcon icon={faCheck} className="text-primary-blue-600" />
      )}
    </>
  );
}

export function CalendarSelectDrawer({
  kalender,
}: {
  kalender: TerminKalender[];
}) {
  const { t } = useTranslation();
  const { form } = useCreateTermineFormContext();
  const [open, setOpen] = useState(false);

  return (
    <form.AppField name="terminKalenderId">
      {(field) => {
        const selectedKalender = kalender.find(
          (k) => k.id === field.state.value,
        );
        const displayText = selectedKalender?.name;
        return (
          <FieldBase label={t('domain.termine.terminCalendar')}>
            <Drawer
              open={open}
              onOpenChange={(isOpen) => {
                setOpen(isOpen);
              }}>
              <DrawerTrigger asChild>
                <Button
                  variant="outline-secondary"
                  className="flex items-center justify-between w-full">
                  <span className="flex items-center gap-2 text-sm">
                    {selectedKalender.markierung &&
                      selectedKalender.markierung !==
                        markierungGermanMap['LIGHTGRAY'] &&
                      selectedKalender.id !== 'NO_KALENDER' && (
                        <Markierung
                          color={
                            markierungMapper[selectedKalender.markierung].base
                          }
                        />
                      )}
                    {displayText}
                  </span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="text-neutrals-400"
                  />
                </Button>
              </DrawerTrigger>
              <DrawerContent className="flex flex-col">
                <DrawerHeader className="px-3 pb-0">
                  <DrawerTitle className="text-sm text-center text-neutrals-900 font-semibold pt-2">
                    {t('domain.termine.terminCalendar')}
                  </DrawerTitle>
                  <DrawerDescription className="sr-only">
                    {t('domain.termine.terminCalendar')}
                  </DrawerDescription>
                </DrawerHeader>
                <ScrollArea
                  className="flex-1 p-4 overflow-scroll max-h-[60vh]"
                  data-vaul-no-drag>
                  <div className="space-y-0">
                    {kalender.map((kalenderItem, idx) => {
                      const isSelected = field.state.value === kalenderItem.id;
                      return (
                        <div key={kalenderItem.id}>
                          <Button
                            variant="ghost"
                            aria-selected={isSelected}
                            onClick={() => {
                              field.handleChange(kalenderItem.id);
                              setOpen(false);
                            }}
                            className="w-full justify-between">
                            <CalendarSelectItem
                              kalenderItem={kalenderItem}
                              isSelected={isSelected}
                            />
                          </Button>
                          {idx < kalender.length - 1 && (
                            <div className="border-b border-neutrals-300" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </DrawerContent>
            </Drawer>
          </FieldBase>
        );
      }}
    </form.AppField>
  );
}
